import { useCallback, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import type {
	ExtensionConfiguration,
	ToolbarExtensionConfiguration,
} from '@atlaskit/editor-plugin-selection-extension';
import AddIcon from '@atlaskit/icon/core/add';
import NoteIcon from '@atlaskit/icon/core/note';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useFullPageEditorApi } from '@confluence/full-page-editor-store';
import { useNotesData } from '@confluence/notes-util/entry-points/hooks/useNotesData';
import { useNotesActions } from '@confluence/notes-util/entry-points/NotesStore';
import { useContentType, usePageContentId, usePageSpaceKey } from '@confluence/page-context';
import { useCopyContentLink } from '@confluence/copy-content-link';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage';

const i18n = defineMessages({
	createNote: {
		id: 'note-selection-extension.create-note-button',
		defaultMessage: 'Create a note',
		description: 'Button to create a note from the selected content',
	},
});

const processTextToParagraphs = (text: string) => {
	return text.split('\n').map((section) => ({
		type: 'paragraph',
		content: !!section ? [{ type: 'text', text: section }] : [],
	}));
};

export const useNoteSelectionExtension = () => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { createNote } = useNotesData();
	const { setActiveNoteId } = useNotesActions();
	const [contentId] = usePageContentId();
	const [spaceKey] = usePageSpaceKey();
	const [contentType] = useContentType();
	const isEditRoute = useIsEditorPage();
	const { makeContentLink } = useCopyContentLink({
		pageMode: isEditRoute ? 'edit' : 'view',
		contentType: contentType ?? '',
		contentId: contentId ?? '',
		spaceKey: spaceKey ?? '',
		source: 'useNoteSelectionExtension',
	});
	const pageUrl = makeContentLink() ?? '';
	const [{ fullPageEditorPresetAPI }] = useFullPageEditorApi();
	const editorApiRef = useRef(fullPageEditorPresetAPI);
	editorApiRef.current = fullPageEditorPresetAPI;

	const fireAddToNoteAnalytics = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'useNoteSelectionExtension',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'addToNote',
			},
		}).fire();
	}, [createAnalyticsEvent]);

	const handleAddToNoteClick = useCallback(
		async (selectedText: string) => {
			fireAddToNoteAnalytics();

			const newAdf = {
				version: 1,
				type: 'doc',
				content: [
					{
						type: 'blockquote',
						content: [
							...processTextToParagraphs(selectedText),
							{
								type: 'paragraph',
								content: [
									{
										type: 'inlineCard',
										attrs: {
											url: pageUrl,
										},
									},
								],
							},
						],
					},
				],
			};

			const { data } = await createNote({
				variables: { body: JSON.stringify(newAdf) },
			});
			setActiveNoteId(data?.createNote?.note?.ari);
		},
		[fireAddToNoteAnalytics, createNote, setActiveNoteId, pageUrl],
	);

	const toolbarConfiguration: ToolbarExtensionConfiguration = {
		getToolbarItem: () => ({
			icon: NoteIcon,
			tooltip: formatMessage(i18n.createNote),
		}),
		getMenuItems: () => {
			const selectedText =
				// @ts-ignore: selectionExtension is a new extension behind a feature gate
				editorApiRef.current?.selectionExtension?.actions?.getSelectionText?.().text ?? '';

			return [
				// TODO: Show linked notes
				// {
				// 	label: 'Lorem ipsum',
				// 	icon: NoteIcon,
				// 	onClick: () => alert('Lorem ipsum clicked'),
				// },
				{
					label: formatMessage(i18n.createNote),
					icon: AddIcon,
					onClick: () => handleAddToNoteClick(selectedText),
				},
			];
		},
	};

	const noteSelectionExtension: ExtensionConfiguration = {
		key: 'note-selection-extension',
		source: 'first-party',
		inlineToolbar: toolbarConfiguration,
	};

	return {
		extensionList: [noteSelectionExtension],
	};
};
